import React, { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { cnMerge } from '../../utils/twmerge'
import { HiOfficeBuilding } from 'react-icons/hi'

export interface SectorOption {
  value: string | number
  label: string
  disabled?: boolean
  nome_setor?: string
  descricao?: string
}

export interface SectorSelectProps {
  options: SectorOption[]
  value?: string | number
  onChange?: (value: string | number) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  searchable?: boolean
  onSearch?: (query: string) => void
  error?: boolean
}

export const SectorSelect: React.FC<SectorSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = "Selecione um setor...",
  className,
  disabled = false,
  searchable = true,
  onSearch,
  error = false
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedOption, setSelectedOption] = useState<SectorOption | null>(
    options.find(opt => opt.value === value) || null
  )
  const [dropdownCoords, setDropdownCoords] = useState({ top: 0, left: 0, width: 0 })
  const selectRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  const filteredOptions = searchable && searchQuery
    ? options.filter(option => 
        option.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
        option.nome_setor?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        option.descricao?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : options

  // Sincronizar selectedOption quando value mudar externamente
  useEffect(() => {
    const newSelectedOption = options.find(opt => opt.value === value) || null
    setSelectedOption(newSelectedOption)
  }, [value, options])

  // Calcular posição do dropdown quando aberto
  useEffect(() => {
    if (isOpen && selectRef.current) {
      const rect = selectRef.current.getBoundingClientRect()
      const viewport = {
        width: window.innerWidth,
        height: window.innerHeight
      }
      
      const dropdownHeight = Math.min(300, filteredOptions.length * 60 + 40 + 8)
      const spaceBelow = viewport.height - rect.bottom
      const spaceAbove = rect.top
      
      let top = rect.bottom + window.scrollY + 4 // 4px gap (mt-1)
      
      // Se não há espaço suficiente abaixo, mostrar acima
      if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
        top = rect.top + window.scrollY - dropdownHeight - 4
      }
      
      setDropdownCoords({
        top,
        left: rect.left + window.scrollX,
        width: rect.width
      })
    }
  }, [isOpen, filteredOptions.length])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        selectRef.current && 
        !selectRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
        setSearchQuery('')
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
    
    return () => {}
  }, [isOpen])

  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [isOpen, searchable])

  useEffect(() => {
    if (searchable && onSearch) {
      onSearch(searchQuery)
    }
  }, [searchQuery, searchable, onSearch])

  const handleOptionClick = (option: SectorOption) => {
    if (option.disabled) return
    
    setSelectedOption(option)
    onChange?.(option.value)
    setIsOpen(false)
    setSearchQuery('')
  }

  return (
    <>
      <div className={cnMerge("relative", className)} ref={selectRef}>
        {/* Trigger Button */}
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={cnMerge(
            // Base styles
            'flex h-12 w-full items-center justify-between rounded-md border bg-white px-3 py-2 text-sm',
            // Normal border styles
            !error && 'border-gray-300 dark:border-neutral-600',
            // Error border styles
            error && 'border-red-500 dark:border-red-500',
            // Dark mode
            'dark:bg-neutral-800 dark:text-gray-100',
            // Focus styles
            'focus-visible:outline-none focus-visible:ring-2',
            !error && 'focus-visible:ring-blue-500/20 focus-visible:border-blue-500',
            error && 'focus-visible:ring-red-500/20 focus-visible:border-red-500',
            // Transition
            'transition-colors duration-200',
            // Disabled styles
            'disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-gray-50 dark:disabled:bg-neutral-700',
            // Hover
            !error && 'hover:border-gray-400 dark:hover:border-neutral-500',
            error && 'hover:border-red-600 dark:hover:border-red-400',
            // Open state
            isOpen && !error && 'border-blue-500 ring-2 ring-blue-500/20',
            isOpen && error && 'border-red-500 ring-2 ring-red-500/20'
          )}
        >
          <div className="flex items-center gap-3">
            {selectedOption ? (
              <>
                {/* Icon */}
                <div className="flex-shrink-0">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30">
                    <HiOfficeBuilding className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  </div>
                </div>
                {/* Sector Info */}
                <div className="flex flex-col text-left">
                  <span className="text-gray-900 dark:text-gray-100 font-medium">
                    {selectedOption.nome_setor || selectedOption.label}
                  </span>
                  {selectedOption.descricao && (
                    <span className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[300px]">
                      {selectedOption.descricao}
                    </span>
                  )}
                </div>
              </>
            ) : (
              <span className="text-gray-500 dark:text-gray-400">{placeholder}</span>
            )}
          </div>
          <svg
            className={cnMerge(
              'h-4 w-4 text-gray-400 dark:text-gray-500 transition-transform duration-200',
              isOpen && 'rotate-180'
            )}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {/* Dropdown usando Portal */}
      {isOpen && createPortal(
        <div 
          ref={dropdownRef}
          className="fixed z-[9999] rounded-md border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 shadow-lg"
          style={{
            top: `${dropdownCoords.top}px`,
            left: `${dropdownCoords.left}px`,
            width: `${dropdownCoords.width}px`
          }}
        >
          {/* Search Input */}
          {searchable && (
            <div className="border-b border-gray-200 dark:border-neutral-700 p-2">
              <div className="flex items-center gap-2 px-2">
                <svg 
                  className="w-4 h-4 text-gray-400 dark:text-gray-500" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Buscar setor..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full border-0 bg-transparent p-2 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-0"
                />
              </div>
            </div>
          )}

          {/* Options */}
          <div className="max-h-64 overflow-auto py-1">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleOptionClick(option)}
                  disabled={option.disabled}
                  className={cnMerge(
                    'flex w-full items-center gap-3 px-3 py-3 text-left transition-colors',
                    'hover:bg-gray-50 dark:hover:bg-neutral-700 focus:bg-gray-50 dark:focus:bg-neutral-700 focus:outline-none',
                    option.disabled && 'cursor-not-allowed opacity-50',
                    selectedOption?.value === option.value && 'bg-blue-50 dark:bg-blue-900/30'
                  )}
                >
                  {/* Icon */}
                  <div className="flex-shrink-0">
                    <div className={cnMerge(
                      "flex h-8 w-8 items-center justify-center rounded-full",
                      selectedOption?.value === option.value 
                        ? "bg-blue-100 dark:bg-blue-900/50" 
                        : "bg-gray-100 dark:bg-neutral-600"
                    )}>
                      <HiOfficeBuilding className={cnMerge(
                        "w-4 h-4",
                        selectedOption?.value === option.value
                          ? "text-blue-600 dark:text-blue-400"
                          : "text-gray-600 dark:text-gray-300"
                      )} />
                    </div>
                  </div>
                  {/* Sector Info */}
                  <div className="flex flex-col flex-1 min-w-0">
                    <span className={cnMerge(
                      "font-medium",
                      selectedOption?.value === option.value 
                        ? 'text-blue-600 dark:text-blue-400' 
                        : 'text-gray-900 dark:text-gray-100'
                    )}>
                      {option.nome_setor || option.label}
                    </span>
                    {option.descricao && (
                      <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {option.descricao}
                      </span>
                    )}
                  </div>
                </button>
              ))
            ) : (
              <div className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400 text-center">
                Nenhum setor encontrado
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  )
}
